<template>
  <div
    class="cfg-card next-step"
    :class="`tone-${step.tone}`"
  >
    <div class="next-head">
      <div>
        <div class="next-label">
          {{ step.who === 'tecnitex' ? $t('configurator.next_step.label_tecnitex') : step.who === 'client' ? $t('configurator.next_step.label_client') : $t('configurator.next_step.label') }}
        </div>
        <div class="next-title">
          {{ step.title }}
        </div>
        <div
          v-if="step.text"
          class="next-text"
        >
          {{ step.text }}
        </div>
      </div>
      <div class="next-actions">
        <a
          v-if="trackingUrl && ['send', 'cast', 'delivered'].includes(status)"
          :href="trackingUrl"
          target="_blank"
          class="btn btn-outline-secondary cfg-btn"
        >
          <feather-icon
            icon="TruckIcon"
            size="14"
          />
          {{ $t('configurator.detail.track_shipment') }}
        </a>
        <a
          v-if="status === 'budget' && order.budget"
          :href="order.budget.path"
          target="_blank"
          class="btn btn-outline-secondary cfg-btn"
        >
          <feather-icon
            icon="DownloadIcon"
            size="14"
          />
          {{ $t('configurator.next_step.download_quote') }}
        </a>
        <b-button
          v-if="status === 'pending'"
          :variant="isSuperAdmin ? 'outline-secondary' : 'primary'"
          class="cfg-btn"
          @click="$router.push({ name: 'editConfigurator', params: { id: order.id } })"
        >
          <feather-icon
            icon="EditIcon"
            size="14"
          />
          {{ $t('configurator.detail.edit_in_configurator') }}
        </b-button>
        <b-button
          v-if="canAcceptBudget"
          variant="success"
          class="cfg-btn btn-finish"
          @click="acceptBudget"
        >
          <feather-icon
            icon="CheckIcon"
            size="14"
          />
          {{ $t('configurator.detail.accept_proposal') }}
        </b-button>
        <b-button
          v-if="isSuperAdmin && status === 'budget_admin'"
          variant="success"
          class="cfg-btn btn-finish"
          @click="acceptOrder"
        >
          <feather-icon
            icon="CheckIcon"
            size="14"
          />
          {{ $t('configurator.detail.accept_order') }}
        </b-button>
      </div>
    </div>

    <!-- Tecnitex: revisión como checklist -->
    <div
      v-if="isSuperAdmin && status === 'revision'"
      class="next-body"
    >
      <div
        class="check"
        :class="{ done: !pendingLines }"
      >
        <span class="check-mark" />
        <div class="check-info">
          <div class="check-title">
            {{ $t('configurator.next_step.check_prices') }}
          </div>
          <div class="cfg-hint">
            {{ pendingLines ? $tc('configurator.next_step.check_prices_pending', pendingLines, { count: pendingLines }) : $t('configurator.next_step.check_prices_done') }}
          </div>
        </div>
        <a
          v-if="pendingLines"
          href="#"
          class="check-link"
          @click.prevent="$emit('go-lines')"
        >{{ $t('configurator.next_step.go_to_lines') }} ↓</a>
      </div>

      <div
        class="check"
        :class="{ done: transportDone }"
      >
        <span class="check-mark" />
        <div class="check-info">
          <div class="check-title">
            {{ $t('configurator.transport') }}
          </div>
          <div class="cfg-hint">
            {{ pickup ? $t('configurator.next_step.check_transport_pickup') : $t('configurator.next_step.check_transport_help') }}
          </div>
        </div>
        <div
          v-if="!pickup"
          class="check-field"
        >
          <b-form-input
            v-model="form.transport"
            type="number"
            min="0"
            step="0.01"
            size="sm"
            placeholder="€"
          />
          <b-button
            variant="primary"
            class="cfg-btn"
            :disabled="form.transport === ''"
            @click="setTransport(form.transport)"
          >
            {{ $t('configurator.detail.save') }}
          </b-button>
        </div>
      </div>

      <div
        v-if="order.install"
        class="check"
        :class="{ done: Number(order.installation_costs) > 0 }"
      >
        <span class="check-mark" />
        <div class="check-info">
          <div class="check-title">
            {{ $t('configurator.installation') }}
          </div>
          <div class="cfg-hint">
            {{ $t('configurator.next_step.check_installation_help') }}
          </div>
        </div>
        <div class="check-field">
          <b-form-input
            v-model="form.installation"
            type="number"
            min="0"
            step="0.01"
            size="sm"
            placeholder="€"
          />
          <b-button
            variant="primary"
            class="cfg-btn"
            :disabled="form.installation === ''"
            @click="setInstallation(form.installation)"
          >
            {{ $t('configurator.detail.save') }}
          </b-button>
        </div>
      </div>

      <a
        href="#"
        class="settings-toggle"
        @click.prevent="showSettings = !showSettings"
      >
        <feather-icon
          :icon="showSettings ? 'ChevronDownIcon' : 'ChevronRightIcon'"
          size="14"
        />
        {{ $t('configurator.next_step.settings') }}
      </a>
      <div
        v-if="showSettings"
        class="settings"
      >
        <div class="check optional">
          <span class="check-mark" />
          <div class="check-info">
            <div class="check-title">
              {{ $t('configurator.detail.global_discount') }}
            </div>
          </div>
          <div class="check-field">
            <b-form-input
              v-model="form.discount"
              type="number"
              min="0"
              max="100"
              step="0.01"
              size="sm"
              placeholder="%"
            />
            <b-button
              variant="primary"
              class="cfg-btn"
              :disabled="form.discount === ''"
              @click="setDiscount(form.discount)"
            >
              {{ $t('configurator.detail.save') }}
            </b-button>
          </div>
        </div>
        <div class="check optional">
          <span class="check-mark" />
          <div class="check-info">
            <div class="check-title">
              {{ $t('configurator.packaging') }}
            </div>
          </div>
          <div class="check-field">
            <b-form-select
              v-model="form.packagingType"
              :options="packagingOptions"
              size="sm"
              @change="form.packagingPrice = packagingPriceOf(form.packagingType)"
            />
            <b-form-input
              v-model="form.packagingPrice"
              type="number"
              min="0"
              step="0.01"
              size="sm"
              placeholder="€"
            />
            <b-button
              variant="primary"
              class="cfg-btn"
              :disabled="form.packagingPrice === ''"
              @click="setPackaging({ type: form.packagingType, price: form.packagingPrice })"
            >
              {{ $t('configurator.detail.save') }}
            </b-button>
          </div>
        </div>
      </div>

      <div class="finish-bar">
        <span class="cfg-hint">
          {{ remaining ? $tc('configurator.next_step.remaining', remaining, { count: remaining }) : $t('configurator.next_step.ready_to_send') }}
        </span>
        <b-button
          variant="success"
          class="cfg-btn-lg btn-finish"
          @click="finishReview"
        >
          <feather-icon
            icon="SendIcon"
            size="14"
          />
          {{ $t('configurator.next_step.finish_review') }}
        </b-button>
      </div>
    </div>

    <!-- Tecnitex: actualizar el envío -->
    <div
      v-if="isSuperAdmin && ['ready', 'send', 'cast'].includes(status)"
      class="next-body shipment"
    >
      <div>
        <div class="cfg-label">
          {{ $t('configurator.detail.shipment_status') }}
        </div>
        <b-form-select
          v-model="form.shipment.status"
          :options="shipmentOptions"
          size="sm"
        />
      </div>
      <div>
        <div class="cfg-label">
          {{ $t('configurator.detail.shipment_date') }}
        </div>
        <b-form-input
          v-model="form.shipment.date"
          type="date"
          size="sm"
        />
      </div>
      <div class="wide">
        <div class="cfg-label">
          {{ $t('configurator.detail.shipment_url') }}
        </div>
        <b-form-input
          v-model="form.shipment.url"
          size="sm"
          placeholder="https://"
        />
      </div>
      <div class="align-self-end">
        <b-button
          variant="primary"
          class="cfg-btn"
          :disabled="!form.shipment.status"
          @click="updateShipment"
        >
          {{ $t('configurator.detail.update_status') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BButton, BFormInput, BFormSelect } from 'bootstrap-vue'
import { TimestampToFlatPickr } from '@/libs/helpers'
import { offerLines } from '@/views/configurator/offer'

const SHIPMENT_FLOW = ['ready', 'send', 'cast', 'delivered']

// "Siguiente paso": en qué punto está la oferta, quién tiene que actuar y con qué botón
export default {
  components: {
    BButton, BFormInput, BFormSelect,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showSettings: false,
      form: {
        transport: '',
        installation: '',
        discount: '',
        packagingType: 'packaging_standard',
        packagingPrice: '',
        shipment: { status: null, date: '', url: '' },
      },
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'auth/getUser',
    }),
    isSuperAdmin() {
      return this.currentUser.roles[0].name === 'super_admin'
    },
    status() {
      return this.order.status_last ? this.order.status_last.type : 'pending'
    },
    pickup() {
      return !(this.order.address && this.order.address.address)
    },
    pendingLines() {
      return offerLines(this.order).filter(l => l.attr.price_pending).length
    },
    transportDone() {
      return this.pickup || (!this.order.shipping_pending && Number(this.order.shipping_costs) > 0)
    },
    remaining() {
      let count = this.pendingLines
      if (!this.transportDone) count += 1
      if (this.order.install && !(Number(this.order.installation_costs) > 0)) count += 1
      return count
    },
    // El cliente acepta la propuesta; el super_admin solo las ofertas que ha hecho él
    canAcceptBudget() {
      return this.status === 'budget' && (!this.isSuperAdmin || (this.order.user && this.order.user.id === this.currentUser.id))
    },
    trackingUrl() {
      const entry = [...(this.order.status || [])].reverse().find(s => s.url_seguimiento)
      if (!entry) return null
      return /^https?:\/\//.test(entry.url_seguimiento) ? entry.url_seguimiento : `//${entry.url_seguimiento}`
    },
    deliveredDate() {
      const entry = [...(this.order.status || [])].reverse().find(s => s.type === 'delivered')
      return entry ? TimestampToFlatPickr(entry.created) : ''
    },
    // Mensaje según estado y rol
    step() {
      const t = key => this.$t(`configurator.next_step.${key}`)
      const tecnitex = this.isSuperAdmin
      switch (this.status) {
        case 'pending':
          return tecnitex
            ? {
              who: 'client', tone: 'wait', title: t('pending_tecnitex_title'), text: t('pending_tecnitex_text'),
            }
            : {
              who: 'client', tone: 'action', title: t('pending_client_title'), text: t('pending_client_text'),
            }
        case 'revision':
          return tecnitex
            ? {
              who: 'tecnitex', tone: 'action', title: t('revision_tecnitex_title'), text: t('revision_tecnitex_text'),
            }
            : {
              who: 'tecnitex', tone: 'wait', title: t('revision_client_title'), text: t('revision_client_text'),
            }
        case 'budget':
          return this.canAcceptBudget
            ? {
              who: 'client', tone: 'action', title: t('budget_client_title'), text: t('budget_client_text'),
            }
            : {
              who: 'client', tone: 'wait', title: t('budget_tecnitex_title'), text: t('budget_tecnitex_text'),
            }
        case 'budget_admin':
          return tecnitex
            ? {
              who: 'tecnitex', tone: 'action', title: t('accepted_tecnitex_title'), text: t('accepted_tecnitex_text'),
            }
            : {
              who: 'tecnitex', tone: 'wait', title: t('accepted_client_title'), text: t('accepted_client_text'),
            }
        case 'ready':
        case 'send':
        case 'cast':
          return tecnitex
            ? {
              who: 'tecnitex', tone: 'action', title: t('shipment_tecnitex_title'), text: t('shipment_tecnitex_text'),
            }
            : {
              who: 'tecnitex', tone: 'wait', title: this.$t(`configurator.order_status.${this.status}`), text: t(`shipment_client_${this.status}`),
            }
        case 'delivered':
          return {
            who: null, tone: 'done', title: t('delivered_title'), text: this.$t('configurator.next_step.delivered_text', { date: this.deliveredDate }),
          }
        case 'canceled':
          return {
            who: null, tone: 'done', title: this.$t('configurator.order_status.canceled'), text: '',
          }
        default:
          return {
            who: null, tone: 'wait', title: this.$t(`configurator.order_status.${this.status}`), text: '',
          }
      }
    },
    packagingOptions() {
      return [
        { text: this.$t('configurator.packaging_standard'), value: 'packaging_standard' },
        { text: this.$t('configurator.packaging_wood'), value: 'packaging_wood' },
      ]
    },
    shipmentOptions() {
      return SHIPMENT_FLOW.map(value => ({ value, text: this.$t(`configurator.order_status.${value}`) }))
    },
  },
  watch: {
    order: {
      handler(order) {
        this.form.transport = order.shipping_costs || ''
        this.form.installation = order.installation_costs || ''
        this.form.discount = order.discount_revision_percentage || ''
        this.form.packagingType = order.packaging_type || 'packaging_standard'
        this.form.packagingPrice = this.packagingPriceOf(this.form.packagingType)
        // Propone el siguiente estado del envío
        const index = SHIPMENT_FLOW.indexOf(this.status)
        this.form.shipment = {
          status: SHIPMENT_FLOW[Math.min(index + 1, SHIPMENT_FLOW.length - 1)] || null,
          date: new Date().toISOString().slice(0, 10),
          url: '',
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      changeStatus: 'offer/changeStatus',
      setTransport: 'offer/setTransport',
      setInstallation: 'offer/setInstallation',
      setDiscount: 'offer/setDiscount',
      setPackaging: 'offer/setPackaging',
      finishReviewAction: 'offer/finishReview',
    }),
    packagingPriceOf(type) {
      return (type === 'packaging_wood' ? this.order.packaging_wood : this.order.packaging_standard) || ''
    },
    confirm(message, okTitle) {
      return this.$bvModal.msgBoxConfirm(message, {
        okTitle,
        cancelTitle: this.$t('configurator.cancel'),
        okVariant: 'success',
        cancelVariant: 'outline-secondary',
        footerClass: 'cfg-modal-footer',
        centered: true,
      })
    },
    async acceptBudget() {
      if (await this.confirm(this.$t('configurator.detail.accept_proposal_confirm'), this.$t('configurator.detail.accept_proposal'))) {
        await this.changeStatus({ status: 'budget_admin' })
      }
    },
    async acceptOrder() {
      if (await this.confirm(this.$t('configurator.detail.accept_order_confirm'), this.$t('configurator.detail.accept_order'))) {
        await this.changeStatus({ status: 'ready' })
      }
    },
    async finishReview() {
      const message = this.remaining
        ? this.$t('configurator.next_step.finish_review_pending', { count: this.remaining })
        : this.$t('configurator.detail.finish_review_confirm')
      if (await this.confirm(message, this.$t('configurator.next_step.finish_review'))) {
        await this.finishReviewAction()
      }
    },
    updateShipment() {
      const { status, date, url } = this.form.shipment
      this.changeStatus({ status, date: date ? new Date(date).getTime() / 1000 : '', url })
    },
  },
}
</script>

<style lang="scss" scoped>
.next-step {
  border-left: 4px solid #b8c2cc;
  &.tone-action {
    border-left-color: #ac1a19;
  }
  &.tone-wait {
    border-left-color: #ff9f43;
  }
  &.tone-done {
    border-left-color: #3a7d44;
  }
}
.next-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.25rem;
}
.next-label {
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: #82868b;
}
.next-title {
  margin-top: .15rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1e1e1e;
}
.next-text {
  margin-top: .2rem;
  max-width: 640px;
  font-size: .8125rem;
  color: #6e6b7b;
}
.next-actions {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
}
.btn-finish {
  border-color: #3a7d44 !important;
  background: #3a7d44 !important;
  background-image: none !important;
  color: #fff !important;
}
.next-body {
  padding: .25rem 1.25rem 1.25rem;
  border-top: 1px solid #f0f0f0;
}
.check {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: .75rem;
  padding: .75rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.check-mark {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid #ff9f43;
  border-radius: 50%;
}
.check.done .check-mark {
  border-color: #3a7d44;
  background: #3a7d44;
  &::after {
    content: '✓';
    font-size: .7rem;
    font-weight: 800;
    color: #fff;
  }
}
.check.optional .check-mark {
  border-color: #b8c2cc;
  border-style: dashed;
}
.check-info {
  flex: 1;
  min-width: 200px;
}
.check-title {
  font-size: .875rem;
  font-weight: 600;
  color: #1e1e1e;
}
.check.done .check-title {
  color: #6e6b7b;
}
.check-link {
  font-size: .8rem;
  font-weight: 600;
  color: #ac1a19;
}
.check-field {
  display: flex;
  gap: .5rem;
  input {
    width: 120px;
  }
  select {
    width: 130px;
  }
}
.settings-toggle {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  margin-top: .75rem;
  font-size: .8rem;
  font-weight: 600;
  color: #6e6b7b;
}
.finish-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
}
.shipment {
  display: grid;
  grid-template-columns: 180px 170px 1fr auto;
  gap: 1rem;
  padding-top: 1rem;
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
}
</style>
